/**
 * "You left your keyboard in English."
 *
 * Someone means to type Korean, their IME is still in English, and `중국` lands
 * in the box as `wndrnr`. The letters are not wrong — they are the right *keys*.
 * `w` is where `ㅈ` lives on a Korean keyboard, `n` is where `ㅜ` lives. So the
 * fix is a keyboard-layout transliteration, not a translation.
 *
 * That distinction is why none of this touches the network. The mapping is a
 * fixed table plus the standard Hangul composition automaton: deterministic,
 * exact, and the same answer every time. Asking a model instead would add a
 * round trip to every keystroke, cost money per keystroke, and be *less*
 * accurate — a model can invent a plausible Korean word, a table cannot.
 */

// ── Dubeolsik (두벌식), the layout on every Korean keyboard ──────────────────
const KEY_TO_JAMO: Record<string, string> = {
  q: 'ㅂ', w: 'ㅈ', e: 'ㄷ', r: 'ㄱ', t: 'ㅅ', y: 'ㅛ', u: 'ㅕ', i: 'ㅑ', o: 'ㅐ', p: 'ㅔ',
  a: 'ㅁ', s: 'ㄴ', d: 'ㅇ', f: 'ㄹ', g: 'ㅎ', h: 'ㅗ', j: 'ㅓ', k: 'ㅏ', l: 'ㅣ',
  z: 'ㅋ', x: 'ㅌ', c: 'ㅊ', v: 'ㅍ', b: 'ㅠ', n: 'ㅜ', m: 'ㅡ',
  // Shift: the tense consonants, and the only two vowels that need it.
  Q: 'ㅃ', W: 'ㅉ', E: 'ㄸ', R: 'ㄲ', T: 'ㅆ', O: 'ㅒ', P: 'ㅖ',
};

// ── The three positions in a syllable block, in Unicode order ───────────────
export const CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
export const JUNG = ['ㅏ','ㅐ','ㅑ','ㅒ','ㅓ','ㅔ','ㅕ','ㅖ','ㅗ','ㅘ','ㅙ','ㅚ','ㅛ','ㅜ','ㅝ','ㅞ','ㅟ','ㅠ','ㅡ','ㅢ','ㅣ'];
export const JONG = ['','ㄱ','ㄲ','ㄳ','ㄴ','ㄵ','ㄶ','ㄷ','ㄹ','ㄺ','ㄻ','ㄼ','ㄽ','ㄾ','ㄿ','ㅀ','ㅁ','ㅂ','ㅄ','ㅅ','ㅆ','ㅇ','ㅈ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];

/** ㅗ + ㅏ is not two vowels, it is ㅘ. */
export const VOWEL_PAIRS: Record<string, string> = {
  'ㅗㅏ': 'ㅘ', 'ㅗㅐ': 'ㅙ', 'ㅗㅣ': 'ㅚ',
  'ㅜㅓ': 'ㅝ', 'ㅜㅔ': 'ㅞ', 'ㅜㅣ': 'ㅟ',
  'ㅡㅣ': 'ㅢ',
};

/** Two consonants can share the final slot: 값, 앉, 읽. */
export const FINAL_PAIRS: Record<string, string> = {
  'ㄱㅅ': 'ㄳ',
  'ㄴㅈ': 'ㄵ', 'ㄴㅎ': 'ㄶ',
  'ㄹㄱ': 'ㄺ', 'ㄹㅁ': 'ㄻ', 'ㄹㅂ': 'ㄼ', 'ㄹㅅ': 'ㄽ', 'ㄹㅌ': 'ㄾ', 'ㄹㅍ': 'ㄿ', 'ㄹㅎ': 'ㅀ',
  'ㅂㅅ': 'ㅄ',
};

/** Undoing the above, for when the second half is stolen by the next syllable. */
export const FINAL_SPLIT: Record<string, [string, string]> = Object.fromEntries(
  Object.entries(FINAL_PAIRS).map(([pair, joined]) => [joined, [pair[0], pair[1]]]),
) as Record<string, [string, string]>;

const isVowel = (j: string) => JUNG.includes(j);

/**
 * The jamo a key produces.
 *
 * Only seven keys change under Shift. For every other letter, Shift is
 * irrelevant on a Korean keyboard — `Z` is still `ㅋ` — so an uppercase letter
 * with no shift entry falls back to its lowercase one. Without this, anything
 * typed with caps lock on, or the first letter of a sentence, silently passed
 * through as a Latin character and broke the syllable around it.
 */
export function jamoFor(ch: string): string | undefined {
  return KEY_TO_JAMO[ch] ?? KEY_TO_JAMO[ch.toLowerCase()];
}

export interface Block {
  cho: string;
  jung: string;
  jong: string;
}

const EMPTY: Block = { cho: '', jung: '', jong: '' };

/** A finished block as one character, or its loose pieces if it is incomplete. */
export function renderBlock(b: Block): string {
  if (!b.cho && !b.jung) return b.jong;
  // A vowel with no leading consonant is not a syllable — it stays a bare jamo.
  // That debris is also the signal `looksIntentional` reads to tell Korean
  // typed with the wrong IME from an English word that happens to be typeable.
  if (!b.cho) return b.jung + b.jong;
  if (!b.jung) return b.cho + b.jong;
  const code = 0xac00
    + (CHO.indexOf(b.cho) * 21 + JUNG.indexOf(b.jung)) * 28
    + JONG.indexOf(b.jong);
  return String.fromCharCode(code);
}

/**
 * Keystrokes to Hangul.
 *
 * The rule that makes or breaks this is the **stolen final**. In `thrtkrdla`,
 * `ㅅㅗㄱ` gives `속` — but when a vowel arrives next, that final consonant
 * belongs to the FOLLOWING block: `속` + `ㅏ` is `소` + `가`, not `속아`. With a
 * compound final only the second half moves. Get it wrong and every word with a
 * batchim comes out mangled.
 */
export function keystrokesToHangul(input: string): string {
  let out = '';
  let b: Block = { ...EMPTY };

  const flush = () => { out += renderBlock(b); b = { ...EMPTY }; };

  for (const ch of input) {
    const jamo = jamoFor(ch);
    if (!jamo) {
      // Spaces, punctuation, digits, anything already Korean: close the current
      // block and pass the character through untouched.
      flush();
      out += ch;
      continue;
    }

    if (isVowel(jamo)) {
      if (b.jong) {
        // Steal. A compound final gives up only its second half.
        const split = FINAL_SPLIT[b.jong];
        const stolen = split ? split[1] : b.jong;
        b.jong = split ? split[0] : '';
        flush();
        b = { cho: stolen, jung: jamo, jong: '' };
      } else if (b.jung) {
        const pair = VOWEL_PAIRS[b.jung + jamo];
        if (pair) {
          b.jung = pair;
        } else {
          flush();
          b = { cho: '', jung: jamo, jong: '' };
        }
      } else if (b.cho) {
        b.jung = jamo;
      } else {
        // A vowel with nothing before it cannot start a block.
        flush();
        out += jamo;
      }
      continue;
    }

    // Consonant.
    if (!b.cho && !b.jung) {
      if (CHO.includes(jamo)) b.cho = jamo;
      else { flush(); out += jamo; }   // ㄳ etc. can never lead a block
    } else if (b.cho && !b.jung) {
      // Two consonants in a row: the first was never going to be a syllable.
      flush();
      b.cho = jamo;
    } else if (!b.jong) {
      if (JONG.includes(jamo)) b.jong = jamo;
      else { flush(); b.cho = jamo; }
    } else {
      const pair = FINAL_PAIRS[b.jong + jamo];
      if (pair) {
        b.jong = pair;
      } else {
        flush();
        b.cho = jamo;
      }
    }
  }

  flush();
  return out;
}

// ── Deciding whether to say anything ────────────────────────────────────────

/**
 * Every English word is also a valid sequence of keystrokes, so the automaton
 * will happily turn `with` into `쟈소`. Offering that turns the suggestion into
 * noise people learn to ignore, which costs more than the feature is worth.
 *
 * These are the words that get past the structural checks below — common enough
 * to be typed constantly, and shaped so that they happen to compose cleanly.
 * A frequency list rather than a dictionary: rare English words are not what
 * someone types by accident fifty times a day.
 */
const COMMON_ENGLISH = new Set<string>(`
a about above across after again against all almost alone along already also
always am among an and another any anyone anything are around as ask at away
back bad be because become been before began begin behind being below best
better between big both but buy by call came can cannot car care case change
check child children city close come coming company could country course cut
day days did different do does doing done down during each early easy eat
either else end enough even ever every everyone everything example eye face
fact family far fast father feel few field find fine first five follow food
for form found four free friend from full game gave general get girl give go
god going gone good got great group grow had half hand happen hard has have
he head hear heard help her here herself high him himself his hold home hope
hour house how however i idea if important in into is issue it its itself
just keep kind knew know known land large last late later law lead learn
least leave left less let letter life light like line list little live long
look lot love made make man many may maybe me mean men might mind mine
minute miss moment money month more morning most mother move much music must
my myself name near need never new news next nice night no not note nothing
now number of off often oh old on once one only open or order other others
our out over own page paper part party pass past pay people perhaps person
place plan play please point possible power present problem program public
put question quick quite rather read ready real really reason remember
report rest result right room round run said same saw say school sea second
see seem seen self sell send sense service set several shall she short
should show side since sir sit six small so social some someone something
sometimes son soon sorry sound speak special spend stand start state stay
still stop story street strong study such sure system take talk tell ten
than thank that the their them themselves then there these they thing think
this those though thought three through time to today together told too took
top toward town true try turn two under understand until up upon us use used
usually very view voice wait walk want war was watch water way we week well
went were what when where whether which while white who whole why wide will
win wish with within without woman women word work world would write wrong
year years yes yet you young your yourself
`.trim().split(/\s+/));

export interface HangulSuggestion {
  /** What the active segment spells in Korean. */
  text: string;
  /** The keystrokes being offered a conversion — exactly the slice this replaces. */
  source: string;
  /** Where that slice sits in the input, so a commit can replace only it. */
  start: number;
  end: number;
  /** Syllable count — used only for the confidence gates below. */
  syllables: number;
}

const HANGUL_SYLLABLE = /^[가-힣]$/;
const LETTER = /[A-Za-z]/;
const HANGUL_ANYWHERE = /[가-힣ㄱ-ㅎㅏ-ㅣ]/;

/**
 * The word the caret is sitting at the end of — one unbroken run of letters,
 * and nothing else.
 *
 * Getting this boundary wrong is the difference between a useful feature and an
 * actively harmful one, and it has been wrong twice.
 *
 * First it tested the ENTIRE box, so accepting one suggestion put a Hangul
 * character in the string and every later word was refused: the card appeared
 * once and never came back.
 *
 * Then it spanned any run of "typeable" characters — which included spaces and
 * newlines. That reads a whole paragraph as one word, and one bad token poisons
 * everything after it. The case that found it:
 *
 *     정확한 시간과 장소만 제기되면
 *
 *     wejkrwjkler                    ← typed as a joke; not Korean, not English
 *
 *     gkfdlfdms antngl aksgtmqslek   ← 할일은 무수히 많습니다
 *
 * Each of those last three words converts perfectly on its own. Joined to
 * `wejkrwjkler` across two blank lines they convert to `ㅈ더ㅏㄱ저ㅏㅣㄷㄱ…`,
 * which fails the composition gate — so the user got NO offer on any of them.
 * One piece of nonsense anywhere above the caret silently disabled the feature
 * for the rest of the document.
 *
 * A run of letters cannot do that: nothing outside the word being typed can
 * reach into it. It also makes this the exact mirror of `endsWord` in the
 * Translator — letters continue a word, everything else ends it — so what the
 * keyboard commits and what this offers can never disagree.
 */
function activeSegment(input: string, caret: number): [number, number] | null {
  const end = Math.max(0, Math.min(caret, input.length));
  let start = end;
  while (start > 0 && LETTER.test(input[start - 1])) start--;
  return start < end ? [start, end] : null;
}

/**
 * The language this engine is for.
 *
 * Exported so the caller does not have to hard-code the string `'ko'` when it
 * asks whether this engine applies — the next layout added here (a Japanese
 * romaji engine, a Cyrillic one) declares its own, and the gate in the
 * Translator stays the same line of code.
 */
export const ENGINE_LANG = 'ko';

/**
 * Does a keyboard engine for `engineLang` have any business running on text the
 * app believes is in `believed`?
 *
 * This is the gate that was missing, and it was missing in the one place it
 * matters most. `ehdan` inside `I can speak english and ehdan` is 동무, and the
 * card said so — in a box the app had already labelled "English - Detected", on
 * a tab the user could see. Offering to turn an English sentence into Korean
 * because a Korean layout happens to spell something is the app arguing with a
 * question it has already answered, and it reads as a translator that assumes
 * everyone is Korean.
 *
 * `null` — nothing believed yet — is deliberately permissive. Detection needs a
 * few characters, and the first word of a Korean sentence typed on an English
 * layout is exactly when this feature is worth having; refusing until something
 * is detected would disable it for the case it exists for.
 *
 * The shape generalises on purpose. Every engine added to this file will need
 * the same question asked — a romaji engine must not fire on detected Korean any
 * more than this one may fire on detected English — and a per-engine rule is how
 * two of them end up disagreeing about the same box.
 */
export function engineApplies(engineLang: string, believed: string | null | undefined): boolean {
  return !believed || believed === engineLang;
}

/**
 * Is the line being typed plainly English, on the evidence of the words already
 * finished on it?
 *
 * The gate above depends on the app having formed a belief, and for Latin text
 * that belief comes from the server: `detectScriptClient` reads the alphabet and
 * the alphabet says nothing about which Latin language this is, so until an
 * answer comes back the source tab says "Detect language" and `engineApplies`
 * lets everything through. That window is precisely where the complaint lives —
 * mid-sentence, before the first translation.
 *
 * So the same list gate 3 uses to judge ONE word judges the sentence around it.
 * Two ordinary English words already settled on this line is not a coincidence
 * anybody has to wait for a model to confirm.
 *
 * Two, not one: a single English word inside a Korean sentence is completely
 * normal and must not disable the engine for the rest of the line.
 */
const ENGLISH_WORDS_FOR_CERTAINTY = 2;

function lineLooksEnglish(before: string): boolean {
  let hits = 0;
  for (const word of before.toLowerCase().split(/[^a-z']+/)) {
    if (word && COMMON_ENGLISH.has(word) && ++hits >= ENGLISH_WORDS_FOR_CERTAINTY) return true;
  }
  return false;
}

/**
 * Should the interface offer a conversion for what is being typed right now?
 *
 * Five gates, all of which must pass. Converting is easy; not being annoying is
 * the actual problem.
 *
 * `caret` defaults to the end of the input, which is where it is in every case
 * that matters — passing the real one only adds the ability to fix a word in the
 * middle of a finished sentence.
 *
 * `believed` is the language the app currently thinks the box is in — the source
 * picker when it names one, otherwise whatever detection resolved. See
 * `engineApplies`.
 */
export function hangulSuggestion(
  input: string,
  caret = input.length,
  believed: string | null = null,
): HangulSuggestion | null {
  // Gate 0 — the app already knows what language this is, and it is not this
  // engine's. Cheapest check and the most decisive one, so it goes first.
  if (!engineApplies(ENGINE_LANG, believed)) return null;

  const span = activeSegment(input, caret);
  if (!span) return null;
  const [start, end] = span;
  // Letters only, by construction — no trimming to do and nothing in here that
  // is not convertible.
  const source = input.slice(start, end);
  if (source.length < 2 || source.length > 200) return null;

  const converted = keystrokesToHangul(source);

  // Gate 1 — fully composed. Every character out is a complete syllable, with
  // no orphan ㅗ or ㅣ left standing. Real Korean composes cleanly; English
  // keystrokes usually leave debris. This alone rejects hello, korea, there.
  let syllables = 0;
  for (const ch of converted) {
    if (HANGUL_SYLLABLE.test(ch)) { syllables++; continue; }
    if (/[\s,.!?'"\-()]/.test(ch)) continue;
    return null;
  }

  // Gate 2 — one-syllable hits are where the false positives live: `the` → 솓,
  // `and` → 뭉.
  //
  // Relaxed to a single syllable once there is Hangul in front of the word ON
  // THE SAME LINE. The question this gate exists to answer is "is this person
  // writing English?", and a line that is already Korean has answered it —
  // holding out for two syllables there just refuses to help with the short
  // words (은, 는, 다) that Korean is full of.
  //
  // The line, not the whole box, because the box is where mixed content lives.
  // A Korean paragraph at the top must not lower the bar for an English one
  // three paragraphs down; "am I writing Korean" is a question about here.
  const lineStart = input.lastIndexOf('\n', start - 1) + 1;
  const settled = HANGUL_ANYWHERE.test(input.slice(lineStart, start));
  if (syllables < (settled ? 1 : 2)) return null;

  // Gate 3 — if the word is ordinary English, the person is writing English.
  // This is what catches `when` → 조두 and `with` → 쟈소, which pass gates 1 and 2.
  // Deliberately NOT relaxed for Korean context: dropping an English word into a
  // Korean sentence is completely normal, and mangling it would be worse than
  // saying nothing.
  if (COMMON_ENGLISH.has(source.toLowerCase())) return null;

  // Gate 3b — a Latin capital at the front of the word.
  //
  // Nobody types Korean with the shift key held down. Seven keys do change under
  // Shift on a Dubeolsik keyboard — QWERTOP, the tense consonants and two vowels
  // — and those are real Korean input, so `Qkq` (빱) has to keep working. Every
  // OTHER capital is a letter someone shifted for a reason that only exists in
  // the Latin alphabet: the start of a sentence, or a proper noun.
  //
  // This is what stops `Given by Jordan…` opening as 햪두. `given` is in the list
  // below and `Given` is not, and gate 4 cannot help at the first word of a line
  // because there is nothing in front of it yet to read. The capital is the only
  // evidence available at that point, and it is good evidence.
  if (/^[A-Z]/.test(source) && !(source[0] in KEY_TO_JAMO)) return null;

  // Gate 4 — the words AROUND it are ordinary English, so this one is a name, a
  // typo or a word the list does not carry, and none of those are Korean. The
  // gate that stops `I can speak english and ehdan` offering 동무 without
  // waiting for the server to say what everyone can already see.
  //
  // Skipped once the line has settled Korean on it: there, mixing an English
  // word or two in is normal, and gate 2 has already lowered its own bar for
  // exactly that reason.
  if (!settled && lineLooksEnglish(input.slice(lineStart, start))) return null;

  return { text: converted, source, start, end, syllables };
}
